import { useMemo } from "react";

import { useAnimations } from "@react-three/drei";
import { useDracoGLTF } from "../../../../utils/useDracoGLTF";

import useHashLocation from "../../../../use-hash-location";
import Hotspot from "../../../ui/Hotspot";
import { findAllObjectsStartsWithKey, findObjectStartsWithKey } from "../../../../utils/FindObjects";
import { useTranslation } from "react-i18next";
import MinacInteraction from "./MinacInteraction";
import MinacInteractionRing from "./MinacInteractionRing";
import DynamicTube from "../../DynamicTube";
import { useRemapMaterials } from "../../Materials";
import { Ebbne, MountWithEbbne, useEbbne } from "@solidwhite/ebbnen";
import SetCameraTarget from "../../SetCameraTarget";
import EbbnenLabel from "../../EbbnenLabel";
import { SideBarHider } from "../../../ui/SideBar/SideBar";

const Minac = () => {
  const { nodes, animations, scene } = useDracoGLTF("./models/heat/Minac.glb");

  useRemapMaterials(scene);

  const [_, setLocation] = useHashLocation();

  const { t } = useTranslation();
  const { actions } = useAnimations(animations, scene);

  const cameraTargets = useMemo(() => {
    var obj = findObjectStartsWithKey("CameraTargets_All", scene);
    var camTargets = {};
    obj.children.forEach((c) => {
      if (c.userData.route) camTargets[c.userData.route] = c;
    });

    return camTargets;
  }, []);

  const hotSpots = useMemo(() => {
    var hotspots = [];
    var hsParent;

    hsParent = findObjectStartsWithKey("Labels", scene);

    if (hsParent) {
      hsParent.children.forEach((c) => hotspots.push(c));
    }

    return hotspots;
  }, []);

  const handleLabelClick = (e, hotspot) => {
    e.stopPropagation();
    // setLocation(`/heating/${hotspot.userData.targetId}`);
    setLocation(`/${hotspot.userData.targetId}`);
  };

  const tubePoints1 = useMemo(() => {
    var points = findAllObjectsStartsWithKey("1_Tube_Point", scene);

    points.sort((a, b) => {
      var intA = parseInt(a.name.replace("1_Tube_Point_", ""));
      var intB = parseInt(b.name.replace("1_Tube_Point_", ""));

      if (intA < intB) {
        return -1;
      }
      if (intA > intB) {
        return 1;
      }
      return 0;
    });

    return points;
  }, []);

  const tubePoints2 = useMemo(() => {
    var points = findAllObjectsStartsWithKey("2_Tube_Point", scene);

    points.sort((a, b) => {
      var intA = parseInt(a.name.replace("2_Tube_Point_", ""));
      var intB = parseInt(b.name.replace("2_Tube_Point_", ""));

      if (intA < intB) {
        return -1;
      }
      if (intA > intB) {
        return 1;
      }
      return 0;
    });

    return points;
  }, []);

  return (
    <group>
      <MountWithEbbne mountedWhen={"active-and-below"}>
        <primitive object={scene} />
      </MountWithEbbne>

      <MountWithEbbne>
        <SetCameraTarget settingsObject={cameraTargets["/products/minac"]} />
        <group position={nodes.Minac_1.position} rotation={nodes.Minac_1.rotation} scale={nodes.Minac_1.scale}>
          <Hotspot
            position={hotSpots[2].position}
            text={hotSpots[2].userData.textId ? t(hotSpots[2].userData.textId) : hotSpots[2].name.toUpperCase()}
            topLeft
            onClick={(e) => handleLabelClick(e, hotSpots[2])}
            delay={0}
            id={hotSpots[2].name}
            key={hotSpots[2].name}
          />
        </group>
      </MountWithEbbne>

      <Ebbne id="minac-process1" visibleWithAbove={3}>
        <group position={nodes.Minac_1.position} rotation={nodes.Minac_1.rotation} scale={nodes.Minac_1.scale}>
          <EbbnenLabel hotspot={hotSpots[0]} isAnimation />
        </group>

        <MountWithEbbne>
          <SideBarHider />
          <SetCameraTarget settingsObject={cameraTargets["/products/minac/ring"]} />
          <MinacInteractionRing action={actions["Anim_Minac_Ring"]} scene={scene} />
        </MountWithEbbne>

        <MountWithEbbne mountedWhen={"visible-and-below"}>
          <MinacTube points={tubePoints2} />
        </MountWithEbbne>
      </Ebbne>

      <Ebbne id="minac-process2" visibleWithAbove={3}>
        <group position={nodes.Minac_1.position} rotation={nodes.Minac_1.rotation} scale={nodes.Minac_1.scale}>
          <EbbnenLabel hotspot={hotSpots[1]} isAnimation />
        </group>

        <MountWithEbbne>
          <SideBarHider />
          <SetCameraTarget settingsObject={cameraTargets["/products/minac/brazing"]} />
          <MinacInteraction action={actions["Anim_Minac_Brazing"]} scene={scene} />
        </MountWithEbbne>

        <MountWithEbbne mountedWhen={"visible-and-below"}>
          <MinacTube points={tubePoints1} />
        </MountWithEbbne>
      </Ebbne>
    </group>
  );
};

const MinacTube = ({ points }) => {
  const { active } = useEbbne();

  return <DynamicTube pointRefs={points} segments={100} update={active} tubeRadius={0.01} />;
};

export default Minac;
